import { useState, useEffect, useContext } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Grid, FormControl, InputLabel, Select, MenuItem,
  Box, Typography, IconButton, Divider, Chip, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, CircularProgress, Autocomplete
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { getIcon } from '../../../utils/IconHandler';
import { B2BClientService } from '../../../services/B2BServices';
import { getLocations } from '../../../services/LocationService';
import AuthContext from '../../../context/AuthContext';
import SnackbarUtils from '../../../utils/SnackbarUtils';
import { DISCOUNT_TYPE } from '../../../utils/Enums';
import { formatMoney } from '../../../utils/currency';

const AddProformaModal = ({ open, onClose, onSave }) => {
  const { t } = useTranslation();
  const authContext = useContext(AuthContext);

  const [proforma, setProforma] = useState({
    clientId: '',
    clientName: '',
    branchId: '',
    status: 'DRAFT',
    validityDays: '',
    notes: '',
    items: []
  });

  const [errors, setErrors] = useState({});
  const [newItem, setNewItem] = useState({
    productId: '',
    productName: '',
    quantity: '',
    unitPrice: '',
    discountType: DISCOUNT_TYPE.PERCENTAGE,
    discountValue: '',
    totalPrice: 0,
    isDiscountManuallyModified: false // Flag para indicar si el usuario modificó el descuento
  });

  // Estados para datos reales
  const [clients, setClients] = useState([]);
  const [products, setProducts] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loadingClients, setLoadingClients] = useState(false);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [loadingLocations, setLoadingLocations] = useState(false);

  // Cargar datos cuando se abre el modal
  useEffect(() => {
    if (open) {
      fetchClients();
      fetchLocations();
    }
  }, [open]);

  // Cargar productos cuando cambia la sucursal o el cliente
  useEffect(() => {
    const isValidBranch = locations.some(location => Number(location.id) === Number(proforma.branchId));
    if (open && proforma.branchId && proforma.clientId && isValidBranch) {
      fetchProducts();
    } else if (open && (!proforma.branchId || !proforma.clientId || !isValidBranch)) {
      // Limpiar productos si no hay cliente o sucursal seleccionada
      setProducts([]);
    }
  }, [open, proforma.branchId, proforma.clientId, locations]);

  const fetchClients = async () => {
    try {
      setLoadingClients(true);
      const response = await B2BClientService.getClients(1, 100000, '');
      setClients(Array.isArray(response?.clients) ? response.clients : []);
    } catch (error) {
      setClients([]);
    } finally {
      setLoadingClients(false);
    }
  };

  const fetchLocations = async () => {
    try {
      setLoadingLocations(true);
      
      const locationsData = await getLocations();
      // Filtrar solo las sucursales que NO son plantas (isPlant !== true)
      const filteredLocations = (locationsData || []).filter(location => !location.isPlant);
      
      setLocations(filteredLocations);
      setProforma(prev => {
        const currentBranchIsAvailable = filteredLocations.some(location => Number(location.id) === Number(prev.branchId));
        if (currentBranchIsAvailable) return prev;

        return {
          ...prev,
          branchId: filteredLocations[0]?.id || '',
          items: []
        };
      });
    } catch (error) {
      setLocations([]);
    } finally {
      setLoadingLocations(false);
    }
  };

  const fetchProducts = async () => {
    if (!proforma.branchId || !proforma.clientId) {
      setProducts([]);
      return;
    }

    try {
      setLoadingProducts(true);
      const response = await B2BClientService.getOrderProducts(proforma.clientId, proforma.branchId);
      setProducts(response.products || []);
    } catch (error) {
      console.error('Error fetching products:', error);
      setProducts([]);
    } finally {
      setLoadingProducts(false);
    }
  };

  const handleFieldChange = (field, value) => {
    setProforma(prev => ({ ...prev, [field]: value }));
    setErrors(prev => ({ ...prev, [field]: '' }));
  };

  const handleItemFieldChange = (field, value) => {
    setNewItem(prev => {
      const updated = { ...prev, [field]: value };
      
      // Marcar como modificado manualmente si el usuario cambia el descuento
      if (field === 'discountValue' || field === 'discountType') {
        updated.isDiscountManuallyModified = true;
      }
      
      if (field === 'quantity' || field === 'unitPrice' || field === 'discountValue' || field === 'discountType') {
        const subtotal = updated.quantity * updated.unitPrice;
        let discountAmount = 0;
        
        // Validar que discountValue sea un número válido
        const discountValue = parseFloat(updated.discountValue) || 0;
        
        if (updated.discountType === DISCOUNT_TYPE.PERCENTAGE) {
          discountAmount = subtotal * (discountValue / 100);
        } else if (updated.discountType === DISCOUNT_TYPE.AMOUNT) {
          // El amount es por producto, entonces se multiplica por la cantidad
          const totalDiscountAmount = discountValue * updated.quantity;
          discountAmount = Math.min(totalDiscountAmount, subtotal); // No puede ser mayor al subtotal
        }
        
        updated.totalPrice = subtotal - discountAmount;
        console.log(`Cálculo total - Subtotal: ${formatMoney(subtotal)}, Descuento: ${formatMoney(discountAmount)}, Total: ${formatMoney(updated.totalPrice)}`);
      }
      console.log(`newItem actualizado:`, updated);
      return updated;
    });
  };

  // Función para obtener el precio correcto (siempre precio de sucursal)
  const getCorrectPrice = (productId, originalPrice) => {
    // Siempre usar el precio original de la sucursal
    return originalPrice;
  };

  // Función para obtener el descuento correcto considerando excepciones
  const getCorrectDiscount = (productId, clientId, branchPrice) => {
    const product = products.find(p => p.id === productId);
    return { 
      value: product?.discountValue || 0,
      type: product?.discountType || DISCOUNT_TYPE.PERCENTAGE
    };
  };

  // Función para validar si el formulario está completo
  const isFormValid = () => {
    return proforma.clientId && 
           proforma.branchId &&
           proforma.validityDays && 
           proforma.validityDays > 0 &&
           proforma.items.length > 0;
  };

  const handleAddItem = () => {
    // Validar campos obligatorios
    if (!newItem.productId) {
      SnackbarUtils.error(t('product_required'));
      return;
    }
    if (!newItem.quantity || newItem.quantity <= 0) {
      SnackbarUtils.error(t('quantity_required'));
      return;
    }
    if (!newItem.unitPrice || newItem.unitPrice <= 0) {
      SnackbarUtils.error(t('price_required'));
      return;
    }
    
    if (newItem.productId && newItem.quantity > 0 && newItem.unitPrice > 0) {
      const product = products.find(p => p.id === newItem.productId);
      
      // Obtener el precio correcto (siempre precio de sucursal)
      const correctPrice = getCorrectPrice(newItem.productId, newItem.unitPrice);
      
      // Obtener el descuento correcto considerando excepciones
      const discountInfo = getCorrectDiscount(newItem.productId, proforma.clientId, newItem.unitPrice);
      
      const item = {
        ...newItem,
        productName: product.name,
        unitPrice: correctPrice,
        // Usar los valores de descuento que tiene el usuario (pueden ser modificados manualmente)
        discountValue: newItem.discountValue,
        discountType: newItem.discountType,
        totalPrice: newItem.totalPrice
      };
      
      setProforma(prev => ({
        ...prev,
        items: [...prev.items, item]
      }));
      setNewItem({
        productId: '',
        productName: '',
        quantity: '',
        unitPrice: '',
        discountType: DISCOUNT_TYPE.PERCENTAGE,
        discountValue: '',
        totalPrice: 0,
        isDiscountManuallyModified: false
      });
    }
  };

  const handleRemoveItem = (index) => {
    setProforma(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  const calculateTotal = () => {
    return proforma.items.reduce((sum, item) => sum + item.totalPrice, 0);
  };

  const handleSave = (saveAsDraft = false) => {
    const newErrors = {};
    
    if (!proforma.clientId) {
      newErrors.clientId = t('client_required');
    }

    if (!proforma.branchId) {
      newErrors.branchId = t('branch_required');
    }
    
    if (proforma.items.length === 0) {
      newErrors.items = t('items_required');
    }
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    // Preparar items según B2BProformaItemRequest
    const itemsToSave = proforma.items.map(item => ({
      productId: item.productId,
      quantity: item.quantity,
      unitPrice: item.unitPrice,
      discountType: item.discountType || DISCOUNT_TYPE.PERCENTAGE, // Default PERCENTAGE
      discountValue: item.discountValue || 0.0
    }));

    const proformaToSave = {
      clientId: parseInt(proforma.clientId),
      branchId: parseInt(proforma.branchId),
      status: saveAsDraft ? 'DRAFT' : 'SENT', // Usar enum del backend (DRAFT/SENT)
      validityDays: parseInt(proforma.validityDays),
      notes: proforma.notes || null,
      items: itemsToSave
    };
    
    onSave(proformaToSave);
    setProforma({
      clientId: '',
      clientName: '',
      branchId: '',
      status: 'DRAFT',
      validityDays: '',
      notes: '',
      items: []
    });
    onClose();
  };

  const handleClose = () => {
    setProforma({
      clientId: '',
      clientName: '',
      branchId: '',
      status: 'DRAFT',
      validityDays: '',
      notes: '',
      items: []
    });
    setNewItem({
      productId: '',
      productName: '',
      quantity: '',
      unitPrice: '',
      discountType: DISCOUNT_TYPE.PERCENTAGE,
      discountValue: '',
      totalPrice: 0,
      isDiscountManuallyModified: false
    });
    setErrors({});
    onClose();
  };

  const selectedClient = clients.find(c => c.id === proforma.clientId);

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="lg">
      <DialogTitle>{t('add_proforma')}</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          {/* Información básica */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom sx={{ color: 'primary.main' }}>
              {t('proforma_information')}
            </Typography>
            <Divider sx={{ mb: 2 }} />
          </Grid>

          <Grid item xs={12} md={4}>
            <FormControl fullWidth required error={!!errors.clientId}>
              <InputLabel>{t('client')} *</InputLabel>
              <Select
                value={proforma.clientId}
                label={`${t('client')} *`}
                onChange={e => {
                  const client = clients.find(c => c.id === e.target.value);
                  handleFieldChange('clientId', e.target.value);
                  handleFieldChange('clientName', client?.nombreFantasia || client?.razonSocial || client?.businessName || '');
                  
                  // Limpiar productos cuando cambia el cliente
                  setProducts([]);
                }}
                disabled={loadingClients}
              >
                {loadingClients ? (
                  <MenuItem disabled>
                    <CircularProgress size={20} />
                    {t('loading')}...
                  </MenuItem>
                ) : (
                  clients.map(client => (
                    <MenuItem key={client.id} value={client.id}>
                      {client.nombreFantasia || client.razonSocial || client.businessName || `Cliente ${client.id}`}
                    </MenuItem>
                  ))
                )}
              </Select>
              {errors.clientId && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                  {errors.clientId}
                </Typography>
              )}
            </FormControl>
          </Grid>

          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>{t('branch')}</InputLabel>
              <Select
                value={proforma.branchId}
                label={t('branch')}
                onChange={e => {
                  handleFieldChange('branchId', parseInt(e.target.value));
                  // Limpiar productos cuando cambia la sucursal
                  setProducts([]);
                }}
                disabled={loadingLocations}
              >
                {loadingLocations ? (
                  <MenuItem disabled>
                    <CircularProgress size={20} />
                    {t('loading')}...
                  </MenuItem>
                ) : (
                  locations.map(location => (
                    <MenuItem key={location.id} value={location.id}>
                      {location.name || `Sucursal ${location.id}`}
                    </MenuItem>
                  ))
                )}
              </Select>
              {errors.branchId && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                  {errors.branchId}
                </Typography>
              )}
            </FormControl>
          </Grid>

          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>{t('status')}</InputLabel>
              <Select
                value={proforma.status}
                label={t('status')}
                onChange={e => handleFieldChange('status', e.target.value)}
              >
                <MenuItem value="DRAFT">{t('draft')}</MenuItem>
                <MenuItem value="SENT">{t('sent')}</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              label={t('validity_days')}
              fullWidth
              type="number"
              value={proforma.validityDays || ''}
              onChange={e => handleFieldChange('validityDays', parseInt(e.target.value) || 0)}
              placeholder="30"
              InputProps={{ endAdornment: t('days') }}
              helperText={t('validity_days_help')}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              label={t('notes')}
              fullWidth
              multiline
              rows={3}
              value={proforma.notes}
              onChange={e => handleFieldChange('notes', e.target.value)}
            />
          </Grid>

          {/* Descuento global del cliente */}
          {selectedClient && selectedClient.globalDiscount > 0 && (
            <Grid item xs={12}>
              <Box sx={{ p: 2, bgcolor: 'info.light', borderRadius: 1 }}>
                <Typography variant="body2" color="info.contrastText">
                  {t('client_global_discount')}: {selectedClient.globalDiscount}%
                </Typography>
              </Box>
            </Grid>
          )}

          {/* Items del presupuesto */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', mt: 2 }}>
              {t('proforma_items')}
            </Typography>
            <Divider sx={{ mb: 2 }} />
          </Grid>

          <Grid item xs={12} md={2.5}>
            <Autocomplete
              fullWidth
              options={products}
              getOptionLabel={(option) => option.name || ''}
              value={products.find(p => p.id === newItem.productId) || null}
              onChange={(event, newValue) => {
                if (newValue) {
                  const correctPrice = getCorrectPrice(newValue.id, newValue.price || 0);
                  const discountInfo = getCorrectDiscount(newValue.id, proforma.clientId, newValue.price || 0);
                  
                  console.log(`Producto seleccionado: ${newValue.name}`);
                  console.log(`Precio de sucursal: ${formatMoney(correctPrice)}`);
                  console.log(`Descuento aplicado: ${discountInfo.value} (${discountInfo.type})`);
                  
                  handleItemFieldChange('productId', newValue.id);
                  handleItemFieldChange('productName', newValue.name);
                  handleItemFieldChange('unitPrice', correctPrice);
                  
                  // Solo aplicar descuento automático si el usuario no lo ha modificado manualmente
                  if (!newItem.isDiscountManuallyModified) {
                    handleItemFieldChange('discountValue', discountInfo.value);
                    handleItemFieldChange('discountType', discountInfo.type);
                  }
                } else {
                  handleItemFieldChange('productId', '');
                  handleItemFieldChange('productName', '');
                  handleItemFieldChange('unitPrice', '');
                  // Solo resetear descuento si no fue modificado manualmente
                  if (!newItem.isDiscountManuallyModified) {
                    handleItemFieldChange('discountValue', '');
                    handleItemFieldChange('discountType', DISCOUNT_TYPE.PERCENTAGE);
                  }
                }
              }}
              disabled={loadingProducts || !proforma.clientId || !proforma.branchId}
              loading={loadingProducts}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('product')}
                  placeholder={!proforma.clientId ? t('select_client_first') : !proforma.branchId ? t('select_branch_first') : t('search_product')}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingProducts ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              renderOption={(props, option) => (
                <Box component="li" {...props}>
                  <Box>
                    <Typography variant="body1">
                      {option.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formatMoney(option.price)}{option.category?.name ? ` - ${option.category.name}` : ''}
                    </Typography>
                  </Box>
                </Box>
              )}
              filterOptions={(options, { inputValue }) => {
                const filtered = options.filter(option =>
                  option.name.toLowerCase().includes(inputValue.toLowerCase()) ||
                  (option.category?.name && option.category.name.toLowerCase().includes(inputValue.toLowerCase()))
                );
                return filtered;
              }}
            />
          </Grid>

          <Grid item xs={12} md={1}>
            <TextField
              label={t('quantity')}
              fullWidth
              type="number"
              value={newItem.quantity || ''}
              onChange={e => handleItemFieldChange('quantity', parseInt(e.target.value) || 0)}
              placeholder="0"
            />
          </Grid>

          <Grid item xs={12} md={2.5}>
            <TextField
              label={t('unit_price')}
              fullWidth
              type="number"
              value={newItem.unitPrice || ''}
              onChange={e => handleItemFieldChange('unitPrice', parseFloat(e.target.value) || 0)}
              placeholder="0"
              disabled={true}
            />
          </Grid>

          <Grid item xs={12} md={2.5}>
            <TextField
              label={t('total')}
              fullWidth
              value={newItem.totalPrice}
              InputProps={{ readOnly: true }}
            />
          </Grid>

          <Grid item xs={12} md={2}>
            <Button
              variant="outlined"
              startIcon={getIcon('checkmarkDone')}
              onClick={handleAddItem}
              fullWidth
              sx={{ height: '56px' }}
            >
              {t('add')}
            </Button>
          </Grid>

          {/* Tabla de items */}
          {proforma.items.length > 0 && (
            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom>
                {t('items_list')}
              </Typography>
              <TableContainer component={Paper} sx={{ mb: 2 }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('product')}</TableCell>
                      <TableCell align="center">{t('quantity')}</TableCell>
                      <TableCell align="center">{t('unit_price')}</TableCell>
                      <TableCell align="center">{t('subtotal')}</TableCell>
                      <TableCell align="center">{t('actions')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {proforma.items.map((item, index) => {
                      return (
                        <TableRow key={index}>
                          <TableCell>
                            <Typography variant="body2" fontWeight="bold">
                              {item.productName}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            {item.quantity}
                          </TableCell>
                          <TableCell align="center">
                            {formatMoney(item.unitPrice)}
                          </TableCell>
                          <TableCell align="center">
                            <Typography variant="body2" fontWeight="bold">
                              {formatMoney(item.totalPrice)}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <IconButton 
                              size="small" 
                              onClick={() => handleRemoveItem(index)}
                              color="error"
                            >
                              {getIcon('trash')}
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
              
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Typography variant="h6" color="primary.main">
                  {t('total')}: {formatMoney(calculateTotal())}
                </Typography>
              </Box>
            </Grid>
          )}

          {errors.items && (
            <Grid item xs={12}>
              <Typography variant="caption" color="error">{errors.items}</Typography>
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>{t('cancel')}</Button>
        <Button 
          onClick={() => handleSave(false)} 
          variant="contained"
          disabled={!isFormValid()}
        >
          {t('send_proforma')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddProformaModal;
